/**
 * @file pages/Tours.jsx
 * @description Tours listing page with MakeMyTrip-style category navigation,
 * hero section, and TourList with search and filtering.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import Header from "../Components/Layouts/Header";
import NikabaFooter from "../Components/Layouts/Footer";
import CategoryNav from "../Components/Tours/CategoryNav";
import TourCard from "../Components/Ui/TourCard";
import { Search, SlidersHorizontal, Loader2, MapPin, Sparkles } from 'lucide-react';
import api from "../services/api";
import CustomTripWizard from "../Components/Tours/CustomTripWizard";

const Tours = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || '');
  const [showCustomTrip, setShowCustomTrip] = useState(false);

  const fetchTours = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeCategory !== 'all') params.category = activeCategory;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (sortBy) params.sort = sortBy;

      const { data } = await api.get('/tours', { params });
      const result = data?.data?.tours || data?.tours || [];
      setTours(Array.isArray(result) ? result : []);
      setTotal(data?.total || result.length);
      if (data?.categoryCounts) setCategoryCounts(data.categoryCounts);
      setError(null);
    } catch (err) {
      console.error('Error fetching tours:', err);
      setError('Failed to load tours. Please try again.');
      setTours([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, searchQuery, sortBy]);

  useEffect(() => { fetchTours(); }, [fetchTours]);

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    const params = new URLSearchParams(searchParams);
    if (cat === 'all') params.delete('category');
    else params.set('category', cat);
    setSearchParams(params, { replace: true });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    else params.delete('search');
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
      <Helmet>
        <title>Tours | Nikaba Travels - Handcrafted Journeys</title>
        <meta name="description" content="Explore customized, educational, adventurous, luxury, family, and corporate tours across India's most breathtaking destinations." />
      </Helmet>
      <Header />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#243185] to-[#1a2460] text-white pt-28 pb-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-500 rounded-full opacity-10 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-400 rounded-full opacity-10 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 drop-shadow-lg">Explore Our Tours</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
            Discover handcrafted journeys to India's most breathtaking destinations
          </p>
          {/* Custom Trip CTA */}
          <button
            onClick={() => setShowCustomTrip(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/15 hover:bg-white/25 text-white rounded-full text-sm font-medium backdrop-blur transition-colors mb-6 border border-white/20"
          >
            <Sparkles size={16} /> Design Your Own Trip
          </button>

          {/* Inline search */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto flex gap-2">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search destinations, activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl text-gray-900 bg-white/95 backdrop-blur border-0 focus:ring-2 focus:ring-white/50 outline-none text-sm"
              />
            </div>
            <button type="submit" className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl font-medium text-sm transition-colors">
              Search
            </button>
          </form>
        </div>
      </div>

      {/* Category Navigation */}
      <CategoryNav
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
        categoryCounts={categoryCounts}
      />

      {/* Sort bar + results count */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {loading ? 'Loading...' : `${total} tour${total !== 1 ? 's' : ''} found`}
        </p>
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={14} className="text-gray-400" />
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); const p = new URLSearchParams(searchParams); if (e.target.value) p.set('sort', e.target.value); else p.delete('sort'); setSearchParams(p, { replace: true }); }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">Featured</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="rating">Top Rated</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </div>

      {/* Tours Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                <div className="h-64 bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                  <div className="h-16 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-red-600 text-lg mb-4">{error}</p>
            <button onClick={fetchTours} className="px-6 py-2 bg-[#243185] text-white rounded-lg hover:bg-[#1a2460]">Try Again</button>
          </div>
        ) : tours.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tours.map((tour) => (
              <TourCard key={tour._id} tour={tour} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <MapPin size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg font-medium">No tours found</p>
            <p className="text-gray-400 text-sm mt-1">Try adjusting your search or category filter</p>
          </div>
        )}
      </div>

      {/* Custom Trip Wizard Modal */}
      <CustomTripWizard isOpen={showCustomTrip} onClose={() => setShowCustomTrip(false)} />

      <NikabaFooter />
    </div>
  );
};

export default Tours;

